import { getStoredUserInstituteIds } from '../../../library/Core/SmartFunctions';
import { axiosClient } from '../../../library/Service/axiosClient';

export type RangeKey = 7 | 30 | 90 | 365;

export interface BirthdayAlumni {
    studentUuid: string;
    firstName: string;
    lastName: string;
    profilePictureUrl: string | null;
    currentRole: string | null;
    currentCompany: string | null;
    programName: string | null;
    passOutYear: number;
    city: string | null;
    dateOfBirth: string;
    profileCompletenessScore: number;
    hasProfilePicture: boolean;
}

export interface BirthdayGroup {
    birthdayThisYear: string;
    daysUntil: number;
    alumni: BirthdayAlumni[];
}

export interface BirthdaySearchResponse {
    totalForRange: number;
    tabCounts: Record<RangeKey, number>;
    groups: BirthdayGroup[];
    hasMore: boolean;
    nextOffset: number;
}

export interface BirthdayFilterOptions {
    passOutYears: number[];
    programs: { id: number; name: string }[];
}

export interface BirthdaySearchCriteria {
    rangeKey: RangeKey;
    debouncedSearch: string;
    passOutYear: number | null;
    programId: number | null;
    offset: number;
}

const MS_PER_DAY = 86400000;

class BirthdayService {
    static defaultCriteria(): BirthdaySearchCriteria {
        return {
            rangeKey: 7,
            debouncedSearch: '',
            passOutYear: null,
            programId: null,
            offset: 0,
        };
    }

    static pageLoader(params: any): Promise<any> {
        const criteria = BirthdayService.defaultCriteria();
        return Promise.all([
            BirthdayService.search(criteria, false).catch(() => ({ data: BirthdayService.emptySearchResponse() })),
            BirthdayService.getFilterOptions().catch(() => ({ data: { passOutYears: [], programs: [] } })),
        ]).then(([searchRes, filtersRes]) => ({
            routeInfo: params,
            data: {
                initialResponse: searchRes.data as BirthdaySearchResponse,
                filterOptions: filtersRes.data as BirthdayFilterOptions,
            },
        }));
    }

    static getSessionInstituteIds(): string[] {
        return getStoredUserInstituteIds();
    }

    static search(criteria: BirthdaySearchCriteria, append: boolean): Promise<{ data: BirthdaySearchResponse }> {
        const payload = {
            instituteIds: BirthdayService.getSessionInstituteIds(),
            rangeDays: criteria.rangeKey,
            search: (criteria.debouncedSearch || '').trim(),
            passOutYear: criteria.passOutYear == null ? [] : [criteria.passOutYear],
            programId: criteria.programId == null ? [] : [criteria.programId],
            offset: append ? criteria.offset : 0,
            limit: append ? 10 : 20,
        };
        return axiosClient().post(
            `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/birthdays/search`,
            payload,
        );
    }

    static getFilterOptions(): Promise<{ data: BirthdayFilterOptions }> {
        return axiosClient().post(
            `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}/birthdays/filters`,
            { instituteIds: BirthdayService.getSessionInstituteIds() },
        );
    }

    static emptySearchResponse(): BirthdaySearchResponse {
        return {
            totalForRange: 0,
            tabCounts: { 7: 0, 30: 0, 90: 0, 365: 0 },
            groups: [],
            hasMore: false,
            nextOffset: 0,
        };
    }

    /**
     * Constructs a date for the given year/month/day. Feb 29 in a non-leap year clamps to Feb 28
     * (the spec's chosen behaviour for leap-year birthdays).
     */
    static buildSafeBirthday(year: number, monthZeroBased: number, day: number): Date {
        if (monthZeroBased === 1 && day === 29 && !BirthdayService.isLeapYear(year)) {
            return new Date(year, 1, 28);
        }
        return new Date(year, monthZeroBased, day);
    }

    static isLeapYear(year: number): boolean {
        return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    }

    /** Returns the next occurrence of the alumni's birthday on or after `from` (midnight-aligned, local time). */
    static nextBirthdayDate(birthDateIso: string, from: Date): Date {
        const dob = new Date(birthDateIso);
        const month = dob.getMonth();
        const day = dob.getDate();
        const fromMidnight = BirthdayService.startOfDay(from);
        const year = fromMidnight.getFullYear();
        let candidate = BirthdayService.buildSafeBirthday(year, month, day);
        if (candidate < fromMidnight) {
            candidate = BirthdayService.buildSafeBirthday(year + 1, month, day);
        }
        return candidate;
    }

    static daysUntil(target: Date, from: Date): number {
        const a = BirthdayService.startOfDay(target).getTime();
        const b = BirthdayService.startOfDay(from).getTime();
        return Math.round((a - b) / MS_PER_DAY);
    }

    static startOfDay(d: Date): Date {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }

    static relativeLabel(daysUntil: number): string {
        if (daysUntil === 0) return 'today';
        if (daysUntil === 1) return 'tomorrow';
        return `in ${daysUntil} days`;
    }

    /** Merges an append-page response into the existing list, joining a continuing date group when present. */
    static mergeAppendGroups(existing: BirthdayGroup[], incoming: BirthdayGroup[]): BirthdayGroup[] {
        if (existing.length === 0) return incoming;
        if (incoming.length === 0) return existing;

        const lastExisting = existing[existing.length - 1];
        const firstIncoming = incoming[0];

        if (lastExisting.birthdayThisYear === firstIncoming.birthdayThisYear) {
            const mergedLastGroup: BirthdayGroup = {
                ...lastExisting,
                alumni: [...lastExisting.alumni, ...firstIncoming.alumni],
            };
            return [...existing.slice(0, -1), mergedLastGroup, ...incoming.slice(1)];
        }
        return [...existing, ...incoming];
    }
}

export default BirthdayService;
